import { Box, Button, Grid, Group, Menu, Textarea } from '@mantine/core'
import { IconChevronDown, IconTemplate } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'

import { AIInputWrapper } from '@/components/AIInputWrapper'
import { AISplitButton } from '@/components/AISplitButton'
import { InputLabel } from '@/components/InputLabel'
import { TemplateSelectionCards } from '@/components/TemplateSelectionCards'
import { theme } from '@/configs/theme'
import { useAIFieldGeneration } from '@/hooks/useAIFieldGeneration'
import { useGlobalState } from '@/hooks/useGlobalState'
import { useTemplateSelection } from '@/hooks/useTemplateSelection'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import * as aiClasses from '@/styles/AIInputWrapper.css'
import * as classes from '@/styles/ContentModuleFormScreen.css'
import { getRequiredFieldDescriptionProps } from '@/utils/forms'

export const CustomizeDeliverySection = observer(function CustomizeDeliverySection(props: CMFormSection) {
  const { getFieldState, generateFieldByName, acceptCMSuggestion, rejectSuggestion, regenerateFieldByName } =
    useAIFieldGeneration(props)
  const [, setHasSteps] = useState(false)
  const { api } = useGlobalState()

  // Template selection logic
  const templateSelection = useTemplateSelection({
    form: props.moduleForm,
    fieldName: 'deliveryStyle',
  })

  // Query to fetch presentation styles
  const presentationStylesQuery = useQuery({
    queryKey: ['presentationStyles'],
    queryFn: () => api.getPresentationStyles(),
  })

  // Track if we've already triggered auto-generation to avoid re-triggering
  const autoGenerationTriggeredRef = useRef(false)

  // Track whether the CM had steps when initially loaded (before any user action)
  const hadStepsOnLoadRef = useRef<boolean | null>(null)

  // Field refs for granular scrolling — stored via useRef for stable identity
  const deliveryStyleRef = useRef<HTMLDivElement>(null)
  const personalityRef = useRef<HTMLTextAreaElement>(null)
  const sectionObjectivesRef = useRef<HTMLTextAreaElement>(null)
  const presentationObjectivesRef = useRef<HTMLTextAreaElement>(null)
  const outlineRef = useRef<HTMLTextAreaElement>(null)

  const fieldRefs = useRef({
    deliveryStyle: deliveryStyleRef,
    personality: personalityRef,
    sectionObjectives: sectionObjectivesRef,
    presentationObjectives: presentationObjectivesRef,
    outline: outlineRef,
  }).current

  // Expose field refs through props — only needs to run once
  useEffect(() => {
    if (props.setFieldRefs) {
      props.setFieldRefs(fieldRefs as Record<string, React.RefObject<HTMLElement>>)
    }
  }, [])

  useEffect(() => {
    setHasSteps(!!props.stepsQuery.data?.length)
  }, [props.stepsQuery.data])

  // Capture whether CM had steps on initial load (only set once per CM)
  useEffect(() => {
    if (hadStepsOnLoadRef.current === null && props.stepsQuery.isSuccess) {
      hadStepsOnLoadRef.current = (props.stepsQuery.data?.length ?? 0) > 0
    }
  }, [props.stepsQuery.isSuccess, props.stepsQuery.data?.length])

  // Reset tracking and template state when taskId changes (different content module)
  useEffect(() => {
    autoGenerationTriggeredRef.current = false
    hadStepsOnLoadRef.current = null
    templateSelection.resetState()
  }, [props.taskId, templateSelection.resetState])

  // Auto-generate AI content for empty fields when steps become available
  // DISABLED: Auto-activation of AI generation has been disabled per user request
  // useEffect(() => {
  //   // Only trigger auto-generation if:
  //   // 1. We have steps
  //   // 2. We haven't already triggered auto-generation for this session
  //   // 3. The queries are successful (not loading)
  //   // 4. Triggered by: CM loaded with existing steps OR step saved via "Save & Exit"

  //   // CM had steps when initially loaded - safe to auto-generate immediately
  //   const hadStepsOnLoad = hadStepsOnLoadRef.current === true

  //   // Step just saved via "Save & Exit" button in StepModal
  //   const isStepJustSaved = (props.stepSavedTrigger ?? 0) > 0

  //   // If CM started with 0 steps, only trigger on explicit save signal
  //   // If CM started with steps, trigger immediately on load
  //   const shouldTrigger =
  //     props.stepsQuery.isSuccess && (hadStepsOnLoad || isStepJustSaved) && props.moduleForm.initialized

  //   if (hasSteps && !autoGenerationTriggeredRef.current && shouldTrigger) {
  //     autoGenerationTriggeredRef.current = true

  //     const formValues = props.moduleForm.getValues()

  //     // Check if outline field is empty and trigger AI generation
  //     if (!formValues.outline?.trim()) {
  //       generateFieldByName('cm', 'outline')
  //     }
  //   }
  // }, [hasSteps, props.stepsQuery.isSuccess, props.stepSavedTrigger, generateFieldByName, props.moduleForm])

  // Custom Templates button that behaves like AI split button
  const TemplatesButton = () => {
    if (templateSelection.state.isTemplatePending) {
      // Show accept/reject pattern when template is pending
      return (
        <AISplitButton
          fieldState="pending"
          onAskSidekick={() => {}} // Not used for templates
          onAccept={templateSelection.acceptTemplate}
          onReject={templateSelection.rejectTemplate}
          disabled={presentationStylesQuery.isLoading}
        />
      )
    }

    // Show templates dropdown when idle
    return (
      <Menu shadow="md" position="bottom-end" disabled={presentationStylesQuery.isLoading}>
        <Menu.Target>
          <Button
            variant="filled"
            size="sm"
            rightSection={<IconChevronDown size={16} />}
            leftSection={<IconTemplate size={16} />}
            disabled={presentationStylesQuery.isLoading}>
            Templates
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Choose a template</Menu.Label>
          {presentationStylesQuery.data?.map(template => (
            <Menu.Item key={template.presentationStyleId} onClick={() => templateSelection.selectTemplate(template)}>
              {template.displayName || template.name}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    )
  }

  return (
    <Grid gutter="xl">
      <Grid.Col span={12}>
        <Box ref={fieldRefs.deliveryStyle}>
          {templateSelection.shouldShowTemplateCards() && presentationStylesQuery.data ? (
            // Template selection mode
            <TemplateSelectionCards
              templates={presentationStylesQuery.data}
              onTemplateSelect={templateSelection.selectTemplate}
              isLoading={presentationStylesQuery.isLoading}
              hasError={!!props.moduleForm.errors.deliveryStyle}
            />
          ) : (
            // Text input mode
            <Textarea
              key={props.moduleForm.key('deliveryStyle')}
              placeholder="Describe the delivery style for your Sidekick."
              autosize
              resize="vertical"
              minRows={4}
              {...templateSelection.getInputProps()}
              {...getRequiredFieldDescriptionProps(
                props.moduleForm,
                'deliveryStyle',
                'Define how your Sidekick should deliver content to users.',
              )}
              variant={templateSelection.state.isTemplatePending ? 'filled' : undefined}
              label={
                <Group justify="space-between" align="center">
                  <InputLabel label="Delivery Style" withAsterisk={true} withSpacer={false} />
                  <TemplatesButton />
                </Group>
              }
              classNames={{
                input: theme.cx(
                  classes.mskIntroductionInput,
                  templateSelection.state.isTemplatePending && aiClasses.input,
                ),
              }}
            />
          )}
        </Box>
      </Grid.Col>

      <Grid.Col span={12}>
        <AIInputWrapper
          fieldState={getFieldState('outline', 'cm')}
          onAskSidekick={() => generateFieldByName('cm', 'outline')}
          onAskSidekickWithGuide={instructions =>
            generateFieldByName('cm', 'outline', { user_instructions: instructions })
          }
          onAccept={() => acceptCMSuggestion('outline')}
          onReject={() => rejectSuggestion('outline', 'cm')}
          onRegenerate={() => regenerateFieldByName('cm', 'outline')}>
          <Textarea
            key={props.moduleForm.key('outline')}
            ref={fieldRefs.outline}
            withAsterisk
            label="Outline"
            placeholder="Provide an outline for the content module."
            autosize
            resize="vertical"
            minRows={4}
            {...props.moduleForm.getInputProps('outline')}
            {...getRequiredFieldDescriptionProps(
              props.moduleForm,
              'outline',
              "The outline, which you can manually edit, is the Sidekick's map of your content.",
            )}
            classNames={{ input: theme.cx(classes.mskIntroductionInput) }}
          />
        </AIInputWrapper>
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          key={props.moduleForm.key('personality')}
          ref={fieldRefs.personality}
          label="Personality"
          placeholder="(Optional) Describe the Sidekick personality you want."
          autosize
          resize="vertical"
          minRows={4}
          {...props.moduleForm.getInputProps('personality')}
          {...getRequiredFieldDescriptionProps(
            props.moduleForm,
            'personality',
            'What additional personality characteristics do you want your Sidekick to have?',
          )}
          classNames={{ input: theme.cx(classes.mskIntroductionInput) }}
        />
      </Grid.Col>

      {/* <Grid.Col span={12}>
        <Textarea
          ref={fieldRefs.presentationObjectives}
          label="Objectives"
          placeholder="(Optional) Define top-level objectives for your end users interacting with this module."
          autosize
          resize="vertical"
          minRows={4}
          {...props.moduleForm.getInputProps('presentationObjectives')}
          {...getRequiredFieldDescriptionProps(
            props.moduleForm,
            'presentationObjectives',
            'What is the ultimate goal you want end users to accomplish when they use your content?',
          )}
          classNames={{ input: theme.cx(classes.mskIntroductionInput) }}
        />
      </Grid.Col> */}
    </Grid>
  )
})
